import { v4 as uuid } from 'uuid';

import { BaseStreaming } from '../Streaming';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingService from '../../abstract/StreamingService';
import {
  getQuoteHeader,
  QuoteRequestPayload,
  QuoteSubscribeRequest,
  QuoteUnsubscribeRequest,
  QuoteUnsubscribeResponse,
} from './Messages';
import QuoteSubscription, {
  QuoteOnSnapshot,
  QuoteOnSubscribe,
  QuoteOnUpdate,
} from './QuoteSubscription';

export class QuoteStreaming extends StreamingService<DoryServiceType.QUOTE> {
  private available = true;
  public get isAvailable(): boolean {
    return this.available;
  }
  public set isAvailable(val: boolean) {
    if (val && !this.available) {
      this.available = val;
      this.resubscribe();
    } else {
      this.available = val;
    }
  }

  private subscriptions: {
    [key: string]: QuoteSubscription;
  } = {};

  /**
   * @hidden
   */
  constructor(socket: BaseStreaming) {
    super(DoryServiceType.QUOTE, 'quote', socket);
  }

  /**
   * Subscribe to websocket updates
   *
   * ```js example
   * const rics = [
   *   'TSLA.OQ'
   * ]
   * const fids = [
   *   'x._DSPLY_NAME',
   *   'q._TRDPRC_1',
   *   'q._NETCHNG_1',
   *   'q._PCTCHNG',
   *   'q._TRADE_DATE',
   *   'q._TRDTIM_1',
   * ];
   * function confirmCallback(d, e) {
   * 	console.log('subscription confirmed', JSON.stringify(!e ? d : e));
   * }
   * function snapshotCallback(d, e) {
   * 	console.log('snapshot', JSON.stringify(!e ? d : e));
   * }
   * function updateCallback(d, e) {
   * 	console.log('update', JSON.stringify(!e ? d : e));
   * }
   *
   * const id = dory.Streaming.Quote.subscribe(
   * 	{ rics, fids },
   * 	confirmCallback,
   * 	snapshotCallback,
   * 	updateCallback
   * )
   * ```
   *
   * @param data  Subscription information
   * @param subscribe  Executed when subscribe response is received
   * @param snapshot  Executed when snapshot is received
   * @param update  Executed when update is received
   * @param id  Unique subscription identifier
   */
  public subscribe(
    data: QuoteRequestPayload,
    subscribe: QuoteOnSubscribe,
    snapshot: QuoteOnSnapshot,
    update: QuoteOnUpdate,
    id: string = uuid()
  ): string {
    const source = 'Streaming.Quote.subscribe';
    const missingParams =
      subscribe === undefined || snapshot === undefined || update === undefined
        ? 'Missing required callback, "subscribe" requires callbacks for: \n- subscribe(data, error)\n- snapshot(data, error)\n- update(data, error)'
        : '';

    const errorCallback = this.getSubscribeErrorHandler(
      source,
      subscribe,
      id,
      this.subscriptions[id] !== undefined,
      missingParams
    );
    if (errorCallback) {
      return undefined;
    } else {
      // Create subscription object
      const subscription = new QuoteSubscription(
        id,
        this.SOCKET.EventBus,
        data.rics,
        data.fids,
        subscribe,
        snapshot,
        update
      );
      // Store subscription
      this.subscriptions[id] = subscription;
      // Setup subscription
      subscription.start(this.SOCKET.Timeout);
      // Start subscription
      this.SOCKET.send<QuoteSubscribeRequest>(subscription.SubscribeRequest);
    }

    return id;
  }

  /**
   * Unsubscribe from websocket updates
   *
   * ```js example
   * function unsubscribeCallback(d, e) {
   *   console.log('unsubscribed', JSON.stringify(!e ? d : e));
   * }
   * dory.Streaming.Quote.unsubscribe(id, unsubscribeCallback)
   * ```
   *
   * @param id subscription identifier
   * @param callback Executed when unsubscribe response is received
   */
  public unsubscribe(
    id: string,
    callback?: (e: QuoteUnsubscribeResponse, err?: StreamingError) => void
  ): void {
    const source = 'Streaming.Quote.unsubscribe';
    const subscription = this.subscriptions[id];

    if (!this.SOCKET.isConnected) {
      this.RaiseNotConnectedError(source, callback);
    } else if (!subscription) {
      // no subscription for id found
      if (callback) {
        callback(
          undefined,
          new StreamingError(
            source,
            StreamingErrorCodes.INVALID_ID,
            `No subscription for ID ${id} registered!`
          )
        );
      }
    } else {
      // Remove subscription from storage
      subscription.stop();
      delete this.subscriptions[id];

      this.unsubscribeRics(subscription.subscribed.rics, source, callback);
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public unsubscribeAll(): void {
    const ids = Object.keys(this.subscriptions);
    ids.forEach((id) => {
      this.unsubscribe(id);
    });
  }

  //#region Modify Subscription
  /**
   * Add rics to an existing subscription
   *
   * ```js example
   * dory.Streaming.Quote.addRics(id, ['AAPL.OQ', 'GOOGL.OQ', 'MSFT.OQ']);
   * ```
   *
   * @param id  subscription identifier
   * @param rics  List of rics to add
   */
  public addRics(id: string, rics: string[]): void {
    const subscription = this.subscriptions[id];
    subscription.addRics(rics);
    // Setup subscription
    subscription.restart(this.SOCKET.Timeout, rics);
    // Resend subscription message
    this.SOCKET.send<QuoteSubscribeRequest>(subscription.SubscribeRequest);
  }
  /**
   * Remove rics from an existing subscription
   *
   * ```js example
   * dory.Streaming.Quote.removeRics(id, ['AAPL.OQ', 'MSFT.OQ'])
   * ```
   *
   * @param id  subscription identifier
   * @param rics  List of rics to remove
   */
  public removeRics(id: string, rics: string[]): void {
    const subscription = this.subscriptions[id];
    subscription.removeRics(rics);

    this.unsubscribeRics(rics, 'Streaming.Quote.removeRics');
  }
  /**
   * Add fids to an existing subscription
   *
   * ```js example
   * dory.Streaming.Quote.addFids(id, ['q._ASK', 'q._BID'])
   * ```
   *
   * @param id  subscription identifier
   * @param fids  List of fids to add
   */
  public addFids(id: string, fids: string[]): void {
    const subscription = this.subscriptions[id];
    subscription.addFids(fids);
    // Setup subscription
    subscription.restart(this.SOCKET.Timeout);
    // Resend subscription message
    this.SOCKET.send<QuoteSubscribeRequest>(subscription.SubscribeRequest);
  }
  /**
   * Remove fids from an existing subscription
   *
   * ```js example
   * dory.Streaming.Quote.removeFids(id, ['q._ASK', 'q._BID'])
   * ```
   *
   * @param id  subscription identifier
   * @param fids  List of fids to remove
   */
  public removeFids(id: string, fids: string[]): void {
    const subscription = this.subscriptions[id];
    subscription.removeFids(fids);
  }
  //#endregion

  /**
   * Restart all subscriptions
   *
   * @hidden
   */
  public resubscribe(): void {
    Object.keys(this.subscriptions).forEach((id) => {
      const subscription = this.subscriptions[id];
      subscription.restart(this.SOCKET.Timeout);
      this.SOCKET.send<QuoteSubscribeRequest>(subscription.SubscribeRequest);
    });
  }
  /**
   * @hidden
   *
   * In the WebSocketServer subscriptions for the same ric are merged, so we gather all rics
   * from all subscriptions and filter out the rics that are in use in other subscriptions
   */
  public unsubscribeRics(
    rics: string[],
    source: string,
    callback?: (e: QuoteUnsubscribeResponse, err?: StreamingError) => void
  ): void {
    const subscribedRics = [].concat(
      ...Object.keys(this.subscriptions).map(
        (id) => this.subscriptions[id].subscribed.rics
      )
    );
    // All rics not used in other subscriptions
    const unsubscribeRics = rics.filter((ric) => {
      return subscribedRics.indexOf(ric) === -1;
    });
    const header = getQuoteHeader(DoryMessageType.UNSUBSCRIBE);

    if (unsubscribeRics.length !== 0) {
      const msg: QuoteUnsubscribeRequest = {
        header,
        payload: [
          {
            rics: unsubscribeRics,
          },
        ],
      };

      if (callback) {
        this.SOCKET.EventBus.once(
          DoryServiceType.QUOTE,
          DoryMessageType.UNSUBSCRIBE,
          this.setTimeout(source, this.SOCKET.Timeout, callback)
        );
      }
      this.SOCKET.send(msg);
    } else if (callback) {
      // If there are no rics to unsubscribe, fake a successful response
      callback({
        header,
        payload: {
          status: 200,
          message: 'Successfully un-subscribed.',
        },
      });
    }
  }

  /**
   * @hidden
   * Clear all stored subscription ids
   */
  public clearSubscriptions(): void {
    this.subscriptions = {};
  }
}
